import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { AmpsTask } from "@/hooks/queries/amps-task-types";

// AMPS task catalogue hook (Story 6.4, consuming Story 4.1's endpoint). Wraps
// `apiFetch` + `useQuery` — no manual fetch/useEffect (mirrors useResources /
// useListClients). `skipAuthRefresh` is OMITTED: this is a post-session data call that
// MUST ride the silent-refresh path.
//
// The catalogue is static and server-cached 24h, so `staleTime: Infinity` fetches it
// ONCE per session — every task card reads the same cached set rather than refetching.

/** Root key for the catalogue query. `version: "current"` keeps the cache keyed to the active set. */
export const AMPS_TASKS_QUERY_KEY = "amps-tasks";

export function useAmpsTasks() {
  return useQuery({
    queryKey: [AMPS_TASKS_QUERY_KEY, "current"],
    queryFn: () => apiFetch<AmpsTask[]>("/reference/amps-tasks?version=current"),
    staleTime: Infinity,
  });
}

// --- Catalogue → selection helpers (colocated, pure, unit-testable) -----------
// These replace the throwaway `TASK_CATEGORIES` / `tasksForCategory` from
// `evaluation-mockup/mock-data.ts` (which faked the data) with derivations over the
// REAL catalogue. The endpoint already orders rows by code (group letter, then numeric
// suffix), so first-appearance order yields a stable, sensible category ordering.

/** Distinct category names in catalogue (first-appearance) order — the category-select options. */
export function distinctCategories(tasks: AmpsTask[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const task of tasks) {
    if (!seen.has(task.category)) {
      seen.add(task.category);
      ordered.push(task.category);
    }
  }
  return ordered;
}

/** The catalogue tasks within one category — the specific-task-select options for that card. */
export function tasksInCategory(tasks: AmpsTask[], category: string): AmpsTask[] {
  if (!category) return [];
  return tasks.filter((task) => task.category === category);
}
